import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSidepanelContext,
  NodeDetailsSidepanelContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {DefaultProcessFlowchartNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import FlowchartContextPanel, {
  ContextPanelContent,
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import {useFlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';
import {SidepanelType} from 'ui-library/reactflow/sidepanels/sidepanel-manager/useFlowchartSidepanelManager';

import {getAdapterForNode} from './adapterUtils';
import getAllNodeDetailsSidepanelTabsForTaskNode from './getAllNodeDetailsSidepanelTabsForTaskNode';
import {
  NodeDetailsSidepanelContentBaseProps,
  TaskNodeDetailsSidepanelContentProps,
  NodeSettingsTabKeys,
} from './processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractivePlaceholderNode,
  isInteractiveTaskNode,
  isPlaceholderForAddExistingTaskSchemaData,
  isPlaceholderForTaskSchemaCreateData,
} from './processFlowchartConfigEditorNodeTypes';
import PlaceholderNodeDetailsSidepanelContent from './right-sidepanel/PlaceholderNodeDetailsSidepanelContent';
import TaskNodeDetailsSidepanelContent from './right-sidepanel/TaskNodeDetailsSidepanelContent';
import {LiveStateChanges} from './useFlowchartState/BaseFlowchartConfigStateManager';

// Tabs not found in this array will be shown at the end
const NODE_SETTINGS_TAB_ORDER = [
  NodeSettingsTabKeys.INPUT_OUTPUT_FIELDS,
  NodeSettingsTabKeys.TASK_SCHEMA_DETAILS,
  NodeSettingsTabKeys.FLOWCHART_SETTINGS,
  NodeSettingsTabKeys.FIELD_MAPPING_INFO,
];

type RightSidepanelProps = Omit<NodeDetailsSidepanelContentBaseProps, 'onChangeSelectedFieldId'> &
  Omit<NodeDetailsSidepanelContextValue, 'selectedNode'> & {
    platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>;
    size: SidepanelSize;
    titleOverride?: string;
    liveStateChanges: LiveStateChanges;
    rightSidepanelRef?: React.RefObject<HTMLDivElement>;
    onPanelSizeChange?: (size: SidepanelSize) => void;
    ownerToRegistryMap?: Map<string, string>;
  };

// Exported for testing
export function RightSidepanelContent(
  props: RightSidepanelProps & {
    selectedNode: FlowchartNode;
    selectedFieldId: string | null;
    onChangeSelectedFieldId: (fieldId: string | null) => void;
  }
): JSX.Element {
  const {liveStateChanges} = props;
  const [isAutosaverPaused, setIsAutosaverPaused] = liveStateChanges.isAutosaverPausedState;
  const [isAutosaverBlocked, _setIsAutosaverBlocked] = liveStateChanges.isAutosaverBlockedState;
  const [isSaveInFlight, _setIsSaveInFlight] = liveStateChanges.isSaveInFlightState;
  const [isDirty, _setIsDirty] = liveStateChanges.dirtyState;
  const [isReadonly, _setIsReadonly] = liveStateChanges.readonlyState;

  if (isInteractiveTaskNode(props.selectedNode)) {
    const taskNodeData = props.selectedNode.data;

    const nodeDetailsSidepanelContentProps: TaskNodeDetailsSidepanelContentProps = {
      selectedNodeTaskDefinitionVersionSpec: taskNodeData.taskDefinitionVersionSpec,
      selectedNodeId: props.selectedNode.id,
      flowchartConfigId: props.flowchartConfigId,
      flowchart: props.flowchart,
      selectedFieldId: props.selectedFieldId,
      locked: props.locked,
      onUpdateTaskDefinitionVersion: props.onUpdateTaskDefinitionVersion,
      onChangeSelectedFieldId: props.onChangeSelectedFieldId,
      validationErrors: props.validationErrors,
      isAutosaverPaused,
      setIsAutosaverPaused,
      isAutosaverBlocked,
      isSaveInFlight,
      isDirty,
      isReadonly,
    };

    const tabs = getAllNodeDetailsSidepanelTabsForTaskNode(
      props.selectedNode,
      props.platformFlowchartFeatures
    ).sort((left, right) => {
      const indexLeft = NODE_SETTINGS_TAB_ORDER.indexOf(left.key);
      const indexRight = NODE_SETTINGS_TAB_ORDER.indexOf(right.key);

      const orderLeft = indexLeft === -1 ? NODE_SETTINGS_TAB_ORDER.length : indexLeft;
      const orderRight = indexRight === -1 ? NODE_SETTINGS_TAB_ORDER.length : indexRight;
      return orderLeft - orderRight;
    });

    return (
      <TaskNodeDetailsSidepanelContent
        nodeDetailsSidepanelContentProps={nodeDetailsSidepanelContentProps}
        tabs={tabs}
        isFullWidth={props.size === SidepanelSize.FULL}
      />
    );
  } else if (isInteractivePlaceholderNode(props.selectedNode)) {
    return (
      <ContextPanelContent>
        <PlaceholderNodeDetailsSidepanelContent selectedNode={props.selectedNode} />
      </ContextPanelContent>
    );
  } else {
    const adapter = getAdapterForNode(props.selectedNode);
    if (adapter instanceof DefaultProcessFlowchartNodeAdapter) {
      const content = adapter.nodeDetailsSidepanelContent(props.selectedNode);
      if (content != null) {
        return content;
      }
    }
    assert(
      false,
      `This node type is not supported in the right sidepanel: ${props.selectedNode.data.nodeName}`
    );
  }
}

export default function ProcessFlowchartConfigEditorRightSidepanel(
  props: RightSidepanelProps
): JSX.Element | null {
  const {rightSidepanelRef, onPanelSizeChange, ...contextProps} = props;

  const sidepanelManager = useFlowchartSidepanelManagerContext();

  const handleCloseWithUnsavedChanges = useCallback(async () => {
    await sidepanelManager.changeSidepanel({
      sidepanelChange: {
        sidepanel: null,
      },
    });
  }, [sidepanelManager]);

  const {
    nodeId: effectiveSelectedNodeId,
    fieldId: effectiveSelectedFieldId,
    openSidepanelType: effectiveSidepanelType,
  } = sidepanelManager.getEffectiveState();
  const effectiveSelectedNode = useMemo(
    () => props.interactiveFlowchart.nodes.find((n) => n.id === effectiveSelectedNodeId) ?? null,
    [props.interactiveFlowchart.nodes, effectiveSelectedNodeId]
  );

  const nodeDetailsContextValue = useMemo((): NodeDetailsSidepanelContextValue | null => {
    if (!effectiveSelectedNode) {
      return null;
    }

    return {
      ...contextProps,
      selectedNode: effectiveSelectedNode,
    };
  }, [contextProps, effectiveSelectedNode]);

  const getSidepanelTitle = useCallback(() => {
    if (props.titleOverride) {
      return props.titleOverride;
    }
    if (effectiveSelectedNode == null) {
      return 'Node details';
    } else if (isInteractivePlaceholderNode(effectiveSelectedNode)) {
      if (isPlaceholderForAddExistingTaskSchemaData(effectiveSelectedNode.data.placeholderForNodeTypeData)) {
        return 'Add existing task schema';
      } else if (
        isPlaceholderForTaskSchemaCreateData(effectiveSelectedNode.data.placeholderForNodeTypeData)
      ) {
        return 'Create new task schema';
      } else {
        throw new Error('Unexpected placeholder type');
      }
    } else {
      return `Node ${effectiveSelectedNode.data.nodeName} Details`;
    }
  }, [props.titleOverride, effectiveSelectedNode]);

  const effectiveSidepanelKey = effectiveSelectedNodeId ?? 'right';

  return (
    <FlowchartContextPanel
      outerRef={rightSidepanelRef}
      panelSide={PanelSide.RIGHT}
      sidepanelName={getSidepanelTitle()}
      isOpen={effectiveSidepanelType === SidepanelType.NODE_SETTINGS}
      onClose={handleCloseWithUnsavedChanges}
      size={props.size}
      sidepanelKey="right"
      flowchartType={props.flowchartEditorConfigurator.flowchartTypeName}
      onPanelSizeChange={onPanelSizeChange}
    >
      {nodeDetailsContextValue && effectiveSelectedNode && (
        <NodeDetailsSidepanelContext.Provider key={effectiveSidepanelKey} value={nodeDetailsContextValue}>
          <div
            {...getTestProps({component: 'ProcessFlowchartConfigEditorRightSidepanel', element: 'root'})}
            className={css(styles.fullHeightContentWrapper)}
          >
            <RightSidepanelContent
              {...props}
              selectedNode={effectiveSelectedNode}
              selectedFieldId={effectiveSelectedFieldId}
              onChangeSelectedFieldId={async (fieldId: string | null) => {
                await sidepanelManager.changeSidepanel({
                  sidepanelChange: {
                    sidepanel: SidepanelType.NODE_SETTINGS,
                    nodeId: effectiveSelectedNode.id,
                    fieldId,
                  },
                });
              }}
            />
          </div>
        </NodeDetailsSidepanelContext.Provider>
      )}
    </FlowchartContextPanel>
  );
}

const styles = StyleSheet.create({
  fullHeightContentWrapper: {
    height: '100%',
  },
});
